import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, InputGroup } from "react-bootstrap";
import { extractDomainPrefix } from "../../utils/lightspeedDomain";

interface DomainPrefixModalProps {
  show: boolean;
  onCancel: () => void;
  onSubmit: (domainPrefix: string) => void;
  defaultValue?: string;
}

export const DomainPrefixModal: React.FC<DomainPrefixModalProps> = ({
  show,
  onCancel,
  onSubmit,
  defaultValue = "",
}) => {
  const [input, setInput] = useState(defaultValue);
  const [error, setError] = useState<string | null>(null);
  const parsedPrefix = useMemo(() => extractDomainPrefix(input || ""), [input]);
  const isValid = !!parsedPrefix;

  useEffect(() => {
    if (show) {
      setInput(defaultValue);
      setError(null);
    }
  }, [show, defaultValue]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = parsedPrefix;
    if (!parsed) {
      setError("Enter a valid Lightspeed store URL or domain prefix.");
      return;
    }
    setError(null);
    onSubmit(parsed);
  };

  return (
    <Modal
      show={show}
      onHide={onCancel}
      centered
      className="domain-prefix-modal"
    >
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Enter Lightspeed Store URL</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Label>Store URL or domain prefix</Form.Label>
          <InputGroup>
            <Form.Control
              placeholder="e.g., mamimarkets.retail.lightspeed.app or mamimarkets"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") onCancel();
              }}
              aria-describedby="lightspeed-prefix-help"
            />
            <InputGroup.Text title="Parsed domain prefix">
              Prefix: {parsedPrefix || "—"}
            </InputGroup.Text>
          </InputGroup>
          <Form.Text
            id="lightspeed-prefix-help"
            className={isValid ? "text-success" : "text-danger"}
            aria-live="polite"
          >
            {isValid
              ? "Looks good. We will use this prefix for the connection."
              : "Enter a valid Lightspeed URL or prefix, e.g., mamimarkets.retail.lightspeed.app or mamimarkets."}
          </Form.Text>
          {error && (
            <div style={{ color: "#dc3545", marginTop: "0.5rem" }}>{error}</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!isValid}>
            Continue
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default DomainPrefixModal;
